// fileService.js - File reading and processing utilities
// FIXED: Better PDF support, error handling, and fallbacks

// Read file as text
export const readAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

// Read file as data URL (for images)
export const readAsDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
};

// Read file as ArrayBuffer
export const readAsArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsArrayBuffer(file);
  });
};

// Read PDF - SIMPLIFIED (no external libs needed)
export const readPDF = async (file) => {
  try {
    // Try to read as text (some PDFs are text-based)
    const text = await readAsText(file);
    if (text && text.length > 100) {
      return {
        content: text,
        type: 'pdf',
        name: file.name,
        note: 'Basic text extraction'
      };
    }
  } catch (e) {
    console.log('PDF text extraction failed, using metadata');
  }
  
  // Fallback: just provide metadata
  return {
    content: `PDF File: ${file.name}\nSize: ${(file.size / 1024).toFixed(2)} KB\n\n[PDF content extraction requires pdfjs-dist library. Please install: npm install pdfjs-dist]\n\nYou can ask me questions about this PDF and I'll do my best to help based on the filename and context.`,
    type: 'pdf',
    name: file.name,
    note: 'Metadata only - install pdfjs-dist for full text extraction'
  };
};

// Read Excel file (XLSX)
export const readExcel = async (file) => {
  try {
    const XLSX = await import('xlsx');
    const data = await readAsArrayBuffer(file);
    const workbook = XLSX.read(data, { type: 'array' });
    
    // Convert first sheet to JSON
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
    
    return {
      content: JSON.stringify(jsonData, null, 2),
      type: 'excel',
      sheets: workbook.SheetNames,
      name: file.name
    };
  } catch (error) {
    console.error('Excel reading failed:', error);
    return {
      content: `Excel File: ${file.name}\nSize: ${(file.size / 1024).toFixed(2)} KB\n\n[Excel reading requires xlsx library. Install: npm install xlsx]`,
      type: 'excel',
      error: error.message,
      name: file.name
    };
  }
};

// Read Word file (DOCX)
export const readWord = async (file) => {
  try {
    const mammoth = await import('mammoth');
    const arrayBuffer = await readAsArrayBuffer(file);
    const result = await mammoth.extractRawText({ arrayBuffer });
    
    return {
      content: result.value,
      type: 'word',
      name: file.name
    };
  } catch (error) {
    console.error('Word reading failed:', error);
    return {
      content: `Word File: ${file.name}\nSize: ${(file.size / 1024).toFixed(2)} KB\n\n[Word reading requires mammoth library. Install: npm install mammoth]`,
      type: 'word',
      error: error.message,
      name: file.name
    };
  }
};

// Process file based on type
export const processFile = async (file) => {
  const ext = file.name.split('.').pop().toLowerCase();
  
  console.log(`Processing file: ${file.name} (${ext})`);
  
  try {
    // Images - return as data URL
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
      const dataURL = await readAsDataURL(file);
      return {
        content: `[Image: ${file.name}]\nSize: ${(file.size / 1024).toFixed(2)} KB\nType: ${file.type}\n\nData URL: ${dataURL.substring(0, 100)}...\n\n(Image uploaded successfully. You can ask me to describe it or answer questions about it.)`,
        type: 'image',
        name: file.name,
        dataURL: dataURL
      };
    }
    
    // PDF
    if (ext === 'pdf') {
      return await readPDF(file);
    }
    
    // Excel
    if (['xlsx', 'xls', 'xlsm'].includes(ext)) {
      return await readExcel(file);
    }
    
    // Word
    if (['docx', 'doc'].includes(ext)) {
      return await readWord(file);
    }
    
    // JSON
    if (ext === 'json') {
      const text = await readAsText(file);
      try {
        const json = JSON.parse(text);
        return {
          content: JSON.stringify(json, null, 2),
          type: 'json',
          parsed: json,
          name: file.name
        };
      } catch (e) {
        return {
          content: text,
          type: 'json',
          error: 'Invalid JSON',
          name: file.name
        };
      }
    }
    
    // CSV/TSV
    if (['csv', 'tsv'].includes(ext)) {
      const text = await readAsText(file);
      return {
        content: text,
        type: ext,
        name: file.name
      };
    }
    
    // Default: try to read as text
    const text = await readAsText(file);
    
    // Check if content is readable (not binary)
    const isBinary = /[\x00-\x08\x0B\x0C\x0E-\x1F]/.test(text.substring(0, 1000));
    
    if (isBinary) {
      return {
        content: `Binary File: ${file.name}\nSize: ${(file.size / 1024).toFixed(2)} KB\nType: ${file.type}\n\n[This appears to be a binary file. Content cannot be displayed as text.]`,
        type: 'binary',
        name: file.name
      };
    }
    
    return {
      content: text,
      type: 'text',
      name: file.name
    };
    
  } catch (error) {
    console.error(`Error processing file ${file.name}:`, error);
    return {
      content: `Error reading file: ${file.name}\nError: ${error.message}\n\nPlease try uploading the file again or use a different format.`,
      type: 'error',
      error: error.message,
      name: file.name
    };
  }
};

// Legacy compatibility
export const readFile = processFile;
export { processFile as default };
